<template>
  <section id="meetings" class="my-8" data-testid="meetings-section">
    <div class="flex items-center gap-3 mb-4">
      <Icon name="general.calendar" class="w-6 h-6 text-zinc-700 dark:text-zinc-300" />
      <h2 class="text-xl font-bold text-zinc-900 dark:text-zinc-100">Meetings</h2>
      <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Beta</span>
    </div>
    <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-6">Connect your calendar so meetings join, record, and land in your dashboard automatically.</p>

    <!-- Calendar cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div class="border border-zinc-200 dark:border-zinc-700 rounded-lg p-4" data-testid="google-calendar-card">
        <h3 class="font-semibold text-zinc-900 dark:text-zinc-100">Google Calendar</h3>
        <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
          {{
            calendarsLoading
              ? 'Checking calendar status…'
              : googleConnection
                ? 'Connected'
                : 'Connect your Google Calendar so eligible meetings are recorded automatically.'
          }}
        </p>
        <Button
          :type="googleConnection ? 'link' : 'solid'"
          :variant="googleConnection ? 'red' : 'blue'"
          :text="calendarsLoading ? 'Loading…' : googleConnection ? 'Disconnect' : 'Connect Google Calendar'"
          data-testid="google-calendar-btn"
          :loading="calendarsLoading || busy"
          :disabled="calendarsLoading || busy"
          @click="googleConnection ? disconnect() : connectCalendar('google')"
        />
      </div>

      <div class="border border-zinc-200 dark:border-zinc-700 rounded-lg p-4 opacity-60" data-testid="outlook-calendar-card">
        <h3 class="font-semibold text-zinc-900 dark:text-zinc-100">Outlook</h3>
        <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-3">Coming soon. Outlook calendar support is on the way.</p>
        <Button type="solid" variant="gray" text="Coming soon" data-testid="outlook-calendar-btn" :disabled="true" />
      </div>
    </div>

    <!-- Auto-record toggle (only useful once at least one calendar is connected) -->
    <div
      class="border border-zinc-200 dark:border-zinc-700 rounded-lg p-4"
      :class="{ 'opacity-50 pointer-events-none': calendarsLoading || !hasAnyCalendar }"
      data-testid="meetings-settings-panel"
    >
      <div class="flex items-center justify-between gap-4 meetings-auto-record">
        <span>
          <span class="block font-medium text-zinc-900 dark:text-zinc-100">Auto-record meetings</span>
          <span class="block text-sm text-zinc-600 dark:text-zinc-400">Eligible meetings on your calendar are joined and recorded automatically.</span>
        </span>
        <FormKit v-model="autoRecord" type="toggle" data-testid="auto-record-toggle" />
      </div>
      <div class="flex items-center justify-between gap-4 mt-4 meetings-auto-share">
        <span>
          <span class="block font-medium text-zinc-900 dark:text-zinc-100">Auto-share with attendees</span>
          <span class="block text-sm text-zinc-600 dark:text-zinc-400"
            >When a recording is ready, every attendee automatically receives the link by email.</span
          >
        </span>
        <FormKit v-model="autoShare" type="toggle" data-testid="auto-share-toggle" />
      </div>
      <div class="flex items-center justify-between gap-4 mt-4 meetings-recording-scope">
        <span>
          <span class="block font-medium text-zinc-900 dark:text-zinc-100">Record meetings</span>
          <span class="block text-sm text-zinc-600 dark:text-zinc-400"
            >Choose whether the bot joins all meetings, only meetings with external attendees, or internal-only meetings.</span
          >
        </span>
        <FormKit
          v-model="recordingScope"
          type="select"
          :options="recordingScopeOptions"
          :disabled="!autoRecord"
          data-testid="recording-scope-select"
          :classes="{
            input: 'rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100',
          }"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { Button, Icon } from '@/storybook';
import { useToast } from '@/composables/useToast';
import { useMeetingsIntegration } from '../../composables/useMeetingsIntegration';

const toast = useToast();
const {
  autoRecord,
  autoShare,
  recordingScope,
  recordingScopeOptions,
  busy,
  calendarsLoading,
  connectCalendar,
  disconnect,
  enableMeetingDefaultsForConnectedCalendar,
  googleConnection,
  hasAnyCalendar,
  hydrate,
} = useMeetingsIntegration();

const surfaceRedirectStatus = () => {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const status = params.get('status');
  const connectedCalendar = status === 'connected';
  if (error) {
    toast.error(`Calendar connection failed: ${error}`);
  } else if (connectedCalendar) {
    toast.success('Calendar connected.');
  }
  if (error || status) {
    // Strip the noisy query params so reloads don't re-fire the toast.
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }
  return connectedCalendar;
};

onMounted(async () => {
  const connectedCalendar = surfaceRedirectStatus();
  await hydrate();
  if (connectedCalendar) {
    enableMeetingDefaultsForConnectedCalendar();
  }
});
</script>
